import { Spinner, Text } from 'wss3-forge'
import {
  Brain20Regular,
  CalendarLtr20Regular,
  Checkmark16Regular,
  ErrorCircle16Regular,
  Search20Regular,
  Sparkle20Regular,
} from '@fluentui/react-icons'
import type { ChatActivity } from '../hooks/useChat.js'

const KIND_ICON: Record<ChatActivity['kind'], JSX.Element> = {
  thinking: <Sparkle20Regular />,
  searching: <Search20Regular />,
  memory: <Brain20Regular />,
  planning: <CalendarLtr20Regular />,
  tool: <Sparkle20Regular />,
}

function iconFor(a: ChatActivity): JSX.Element {
  if (a.state === 'done') return <Checkmark16Regular />
  if (a.state === 'error') return <ErrorCircle16Regular />
  return KIND_ICON[a.kind] ?? <Sparkle20Regular />
}

export interface ActivityStripProps {
  activities: ChatActivity[]
}

/**
 * Single live status row at the bottom of MessageList. Shows ONE activity at
 * a time (the agent's current step) with a soft pulsing spinner. Replaces
 * itself as the agent moves between thinking ↔ tool calls. Disappears when
 * the assistant answer lands.
 */
export function ActivityStrip({ activities }: ActivityStripProps) {
  const a = activities[0]
  if (!a) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        paddingInline: 'var(--spacing-xs)',
        animation: 'stark-activity-in 180ms ease-out',
      }}
    >
      <style>{`
        @keyframes stark-activity-in {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes stark-activity-pulse {
          0%, 100% { opacity: 0.65; }
          50%      { opacity: 1; }
        }
      `}</style>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          width: 18,
          height: 18,
          alignItems: 'center',
          justifyContent: 'center',
          color:
            a.state === 'error'
              ? 'var(--color-danger)'
              : a.state === 'done'
                ? 'var(--color-success)'
                : 'var(--brand-primary)',
          flexShrink: 0,
        }}
      >
        {a.state === 'running' ? <Spinner size="sm" /> : iconFor(a)}
      </span>
      <Text
        size="sm"
        color="muted"
        style={{
          animation: a.state === 'running' ? 'stark-activity-pulse 1.6s ease-in-out infinite' : undefined,
        }}
      >
        {a.label}
        {a.state === 'running' ? '…' : ''}
      </Text>
    </div>
  )
}
